/**
 * RunDetail — single-execution drill-down.
 *
 * Streams the execution doc from Firestore directly (same pattern as the
 * /tests live-log modal) so we get logs, status, and step results without a
 * separate backend round-trip. Renders:
 *
 *   - Header KPIs (status, location, operator, RAT, modem, duration)
 *   - Step results table (with links into child executions for AT-level detail)
 *   - Full log viewer (the canonical ExecutionLogViewer used by /tests)
 *   - Artifacts (pcap etc.) — clicking fetches a fresh signed URL
 *
 * Replaces the previous shape that read a non-existent `steps` subcollection
 * via the backend `/reports/runs/:executionId` endpoint and silently linked
 * artifacts to an empty `downloadUrl`.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot, collection, query, type Unsubscribe } from 'firebase/firestore';
import {
  ChevronLeft, MapPin, Radio, Signal, Smartphone, Clock, Activity, ChevronRight,
} from 'lucide-react';
import { db } from '@/config/firebase';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/contexts/AuthContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useToast } from '@/contexts/ToastContext';
import { Card, Button, Badge, Spinner, EmptyState } from '@/components/ui';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/Alert';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { ExecutionLogViewer, type ExecutionLog } from '@/components/execution/ExecutionLogViewer';
import { NetworkDiagnosticsPanel } from '@/components/execution-analyzer/NetworkDiagnosticsPanel';
import VoiceAgentCallPanel from '@/components/reports/VoiceAgentCallPanel';
import { ExecutionArtifacts } from '@/components/artifacts/ExecutionArtifacts';
import { SignalSamplesChart } from '@/components/execution-analyzer/SignalSamplesChart';
import { artifactKindLabel, artifactSkipReason, visibleArtifactSkips } from '@/utils/artifactSkips';
import { fetchArtifactSignedUrl } from '@/services/reportsService';
import type { DiagSummary } from '@simcheck/shared-types';

type ExecutionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

interface StepResult {
  stepId: string;
  primitiveId: string;
  primitiveName: string;
  status: 'success' | 'failure' | 'skipped' | string;
  nestedExecutionId?: string | null;
  completedAt?: string | { toDate?: () => Date };
  error?: string | null;
}

interface Artifact {
  id: string;
  type: string;
  filename: string;
  sizeBytes: number;
  contentType: string;
  uploadedAt?: { toDate?: () => Date };
}

interface ExecutionDoc {
  id: string;
  status: ExecutionStatus;
  flowName?: string;
  flowId?: string;
  organizationId: string;
  startedAt?: { toDate?: () => Date } | string | Date;
  completedAt?: { toDate?: () => Date } | string | Date;
  duration?: number;
  durationMs?: number;
  locationName?: string;
  modemModel?: string;
  modemImei?: string;
  rat?: string;
  registeredNetworkName?: string;
  registeredNetworkMcc?: string;
  registeredNetworkMnc?: string;
  esimProfileName?: string;
  logs?: Array<{ level?: string; message?: string; timestamp?: string | { toDate?: () => Date } }>;
  stepResults?: StepResult[];
  metrics?: {
    cellular?: { attachMs?: number; rsrpDbm?: number; sinrDb?: number };
    data?: { throughputKbps?: number; latencyMsP95?: number; lossPct?: number };
    voice?: { mosWb?: number; setupMs?: number; bearer?: string };
  };
  /** Flow outputs — carry `agentResult` for AI voice-agent runs (transcript + assertions). */
  outputs?: Record<string, any>;
  result?: { outputs?: Record<string, any> };
  diagSummary?: DiagSummary;
  /**
   * Why an artifact was NOT produced, `{ kind: reason }` (e.g.
   * `{ l3_trace: 'diag_warming', pcap: 'at_only_modem' }`). Written by
   * central-server from the edge's capture-session skip report, so a missing
   * artifact renders as a reason instead of a silently-empty panel.
   */
  artifactSkips?: Record<string, string>;
}

// Flow parameters — scalar, non-secret `outputs` keys (flow inputs + Variable
// values, incl. a random_choice pick). Mirrors the server's param filter.
const PARAM_SECRET_SUFFIXES = ['pin', 'otp', 'cvv', 'passcode', 'password', 'secret', 'token', 'apikey', 'accesstoken', 'credential', 'credentials', 'privatekey'];
const EXCLUDED_PARAM_KEYS = new Set(['executionId', 'scenarioExecutionId', 'agentResult', 'callResult']);
function isSecretParamName(name: string): boolean {
  const lower = name.toLowerCase();
  return PARAM_SECRET_SUFFIXES.some((s) =>
    lower === s || lower.endsWith('_' + s) ||
    new RegExp('[a-z0-9]' + s.charAt(0).toUpperCase() + s.slice(1) + '$').test(name));
}
function extractFlowParams(exec: ExecutionDoc | null): Array<[string, string | number | boolean]> {
  const out = exec?.outputs ?? exec?.result?.outputs ?? {};
  const entries: Array<[string, string | number | boolean]> = [];
  if (out && typeof out === 'object') {
    for (const [k, v] of Object.entries(out)) {
      if (EXCLUDED_PARAM_KEYS.has(k) || isSecretParamName(k)) continue;
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') entries.push([k, v]);
    }
  }
  return entries.sort((a, b) => a[0].localeCompare(b[0]));
}

function statusVariant(status: ExecutionStatus): 'success' | 'error' | 'warning' | 'secondary' {
  if (status === 'completed') return 'success';
  if (status === 'failed' || status === 'cancelled') return 'error';
  if (status === 'running') return 'warning';
  return 'secondary';
}

function stepStatusVariant(status: string): 'success' | 'error' | 'secondary' {
  if (status === 'success' || status === 'completed') return 'success';
  if (status === 'failure' || status === 'failed') return 'error';
  return 'secondary';
}

function toDate(raw: any): Date | null {
  if (!raw) return null;
  if (raw instanceof Date) return raw;
  if (typeof raw === 'string') return new Date(raw);
  if (typeof raw?.toDate === 'function') return raw.toDate();
  return null;
}

export default function RunDetail() {
  const fmt = useDateFormat();
  const { executionId } = useParams<{ executionId: string }>();
  const { activeOrganization } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [execution, setExecution] = useState<ExecutionDoc | null>(null);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Subscribe to the execution doc — gives us logs + stepResults + status live
  useEffect(() => {
    if (!executionId) return;
    setLoading(true);
    setError(null);
    const unsubExec: Unsubscribe = onSnapshot(
      doc(db, 'executions', executionId),
      (snap) => {
        if (!snap.exists()) {
          setError('Execution not found');
          setExecution(null);
          setLoading(false);
          return;
        }
        const data = snap.data() as any;
        setExecution({ id: snap.id, ...data });
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );

    const unsubArtifacts: Unsubscribe = onSnapshot(
      query(collection(db, 'executions', executionId, 'artifacts')),
      (snap) => {
        setArtifacts(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      },
      (err) => console.warn('Artifact subscription error:', err),
    );

    return () => { unsubExec(); unsubArtifacts(); };
  }, [executionId]);

  const logs: ExecutionLog[] = useMemo(() => {
    if (!execution?.logs) return [];
    return execution.logs.map((log) => ({
      timestamp: toDate(log.timestamp) ?? new Date(),
      level: (log.level === 'warning' ? 'warn' : (log.level ?? 'info')) as ExecutionLog['level'],
      message: log.message ?? '',
    }));
  }, [execution?.logs]);

  const kpiCards = useMemo(() => {
    if (!execution?.metrics) return [];
    const cards: { label: string; value: string }[] = [];
    const m = execution.metrics;
    if (m.cellular?.attachMs !== undefined) cards.push({ label: 'Attach', value: `${m.cellular.attachMs} ms` });
    if (m.cellular?.rsrpDbm !== undefined) cards.push({ label: 'RSRP', value: `${m.cellular.rsrpDbm} dBm` });
    if (m.cellular?.sinrDb !== undefined) cards.push({ label: 'SINR', value: `${m.cellular.sinrDb} dB` });
    if (m.data?.latencyMsP95 !== undefined) cards.push({ label: 'Latency p95', value: `${m.data.latencyMsP95} ms` });
    if (m.data?.throughputKbps !== undefined) {
      const v = m.data.throughputKbps;
      cards.push({ label: 'Throughput', value: v >= 1000 ? `${(v / 1000).toFixed(2)} Mbps` : `${v} kbps` });
    }
    if (m.data?.lossPct !== undefined) cards.push({ label: 'Loss', value: `${m.data.lossPct.toFixed(2)}%` });
    if (m.voice?.mosWb !== undefined) cards.push({ label: 'MOS (WB)', value: m.voice.mosWb.toFixed(2) });
    if (m.voice?.setupMs !== undefined) cards.push({ label: 'Setup', value: `${m.voice.setupMs} ms` });
    if (m.voice?.bearer) cards.push({ label: 'Bearer', value: m.voice.bearer });
    return cards;
  }, [execution?.metrics]);

  const handleArtifactDownload = async (artifact: Artifact) => {
    if (!activeOrganization?.id || !executionId) return;
    setDownloadingId(artifact.id);
    try {
      const signed = await fetchArtifactSignedUrl(activeOrganization.id, executionId, artifact.id);
      // Same-tab anchor click avoids popup blockers — GCS's
      // Content-Disposition (set on the bucket object) makes the browser
      // download rather than navigate.
      const a = document.createElement('a');
      a.href = signed.signedUrl;
      a.download = signed.filename || artifact.filename || artifact.id;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => a.remove(), 100);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to fetch download link');
    } finally {
      setDownloadingId(null);
    }
  };

  const flowParams = useMemo(() => extractFlowParams(execution), [execution]);
  const operatorLine = execution?.registeredNetworkName ?? null;
  const operatorMccmnc =
    execution?.registeredNetworkMcc && execution?.registeredNetworkMnc
      ? `${execution.registeredNetworkMcc}-${execution.registeredNetworkMnc}`
      : null;
  const startedAtDate = toDate(execution?.startedAt);
  const durationMs = execution?.duration ?? execution?.durationMs ?? null;

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                icon={<ChevronLeft className="w-4 h-4" />}
              >
                Back
              </Button>
            </div>
            <h1 className="text-display font-semibold text-foreground mb-2 truncate">
              {execution?.flowName ?? executionId}
            </h1>
            {execution ? (
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <Badge variant={statusVariant(execution.status)}>{execution.status}</Badge>
                {execution.locationName ? (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {execution.locationName}
                  </span>
                ) : null}
                {operatorLine ? (
                  <span className="inline-flex items-center gap-1">
                    <Signal className="h-3.5 w-3.5" /> {operatorLine}
                    {operatorMccmnc ? ` (${operatorMccmnc})` : ''}
                  </span>
                ) : null}
                {execution.rat ? (
                  <span className="inline-flex items-center gap-1">
                    <Radio className="h-3.5 w-3.5" /> {String(execution.rat).toUpperCase()}
                  </span>
                ) : null}
                {execution.modemModel ? (
                  <span className="inline-flex items-center gap-1">
                    <Smartphone className="h-3.5 w-3.5" /> {execution.modemModel}
                  </span>
                ) : null}
                {execution.esimProfileName ? <span>SIM: {execution.esimProfileName}</span> : null}
                {durationMs !== null ? (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" /> {(durationMs / 1000).toFixed(1)}s
                  </span>
                ) : null}
                {startedAtDate ? <span>{fmt.formatDateTime(startedAtDate)}</span> : null}
              </div>
            ) : null}
          </div>
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Failed to load</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {loading && !execution ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : execution ? (
          <>
            {/* KPIs */}
            {kpiCards.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {kpiCards.map((k) => (
                  <Card key={k.label} padding="sm">
                    <div className="text-caption text-muted-foreground uppercase mb-1">{k.label}</div>
                    <div className="text-h1 font-bold text-foreground">{k.value}</div>
                  </Card>
                ))}
              </div>
            ) : null}

            {/* AI voice-agent transcript + assertions (only for agent-call runs) */}
            <VoiceAgentCallPanel execution={execution} />

            {/* Flow parameters — the inputs + Variable values this run used
                (e.g. a calling-card's random destination). Secrets are stripped. */}
            {flowParams.length > 0 ? (
              <Card padding="none">
                <div className="px-4 py-3 border-b border-border">
                  <div className="text-caption text-muted-foreground uppercase">Flow parameters</div>
                </div>
                <dl className="divide-y divide-border">
                  {flowParams.map(([k, v]) => (
                    <div key={k} className="flex items-start justify-between gap-4 px-4 py-2.5">
                      <dt className="text-sm text-muted-foreground font-mono">{k}</dt>
                      <dd className="text-sm font-medium text-foreground text-right break-all">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </Card>
            ) : null}

            {/* Step results */}
            {execution.stepResults && execution.stepResults.length > 0 ? (
              <Card padding="none">
                <div className="px-4 py-3 border-b border-border">
                  <div className="text-caption text-muted-foreground uppercase">
                    Steps ({execution.stepResults.length})
                  </div>
                </div>
                <ResponsiveTable<StepResult>
                  data={execution.stepResults}
                  keyExtractor={(s) => s.stepId}
                  paginated
                  defaultPageSize={25}
                  onRowClick={(s) => {
                    if (s.nestedExecutionId) navigate(`/reports/runs/${s.nestedExecutionId}`);
                  }}
                  columns={[
                    {
                      key: 'name',
                      header: 'Primitive',
                      sortable: true,
                      sortValue: (s: StepResult) => s.primitiveName,
                      render: (_: unknown, s: StepResult) => (
                        <div className="min-w-0">
                          <div className="font-medium truncate">{s.primitiveName}</div>
                          <div className="text-xs text-muted-foreground font-mono truncate">{s.primitiveId}</div>
                        </div>
                      ),
                    },
                    {
                      key: 'status',
                      header: 'Status',
                      sortable: true,
                      sortValue: (s: StepResult) => s.status,
                      render: (_: unknown, s: StepResult) => (
                        <Badge variant={stepStatusVariant(s.status)}>{s.status}</Badge>
                      ),
                    },
                    {
                      key: 'error',
                      header: 'Error',
                      sortable: true,
                      sortValue: (s: StepResult) => s.error ?? '',
                      render: (_: unknown, s: StepResult) => (
                        <span className="text-sm text-muted-foreground truncate">{s.error ?? '—'}</span>
                      ),
                    },
                    {
                      key: 'drill',
                      header: '',
                      render: (_: unknown, s: StepResult) => (
                        s.nestedExecutionId
                          ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              open <ChevronRight className="h-3.5 w-3.5" />
                            </span>
                          : null
                      ),
                    },
                  ]}
                />
                <div className="px-4 py-2 border-t border-border text-xs text-muted-foreground">
                  Click a step to drill into its child execution (AT log, primitive-level events).
                </div>
              </Card>
            ) : null}

            {/* Logs */}
            <Card padding="none">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <div className="text-caption text-muted-foreground uppercase">
                  Execution log ({logs.length})
                </div>
              </div>
              <div className="p-3">
                {logs.length === 0 ? (
                  <EmptyState
                    title="No log entries"
                    description="This execution didn't record any log output."
                  />
                ) : (
                  <ExecutionLogViewer
                    logs={logs}
                    isLive={execution.status === 'running' || execution.status === 'pending'}
                    autoScroll
                    showFilters
                    showExport
                    maxHeight="600px"
                  />
                )}
              </div>
            </Card>

            {/* Network Diagnostics (decoded DIAG / L3 trace) */}
            <NetworkDiagnosticsPanel
              diag={execution.diagSummary}
              artifacts={artifacts}
              onDownload={(a) => handleArtifactDownload(a as Artifact)}
            />

            {/* Radio conditions sampled across the test window (#252 SC-6) */}
            <SignalSamplesChart samples={(execution as any).signalSamples} />

            {/* Artifacts — voice_wav rows play inline (see ExecutionArtifacts) */}
            {artifacts.length > 0 ? (
              <Card padding="lg">
                <div className="text-caption text-muted-foreground uppercase mb-3">
                  Artifacts ({artifacts.length})
                </div>
                <ExecutionArtifacts
                  artifacts={artifacts}
                  executionId={executionId!}
                  organizationId={activeOrganization?.id}
                  gridClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                />
              </Card>
            ) : null}

            {/* Why an artifact is missing — rendered from executions/{id}.artifactSkips
                so "no signaling / no pcap" shows a reason instead of nothing. Only
                surfaces kinds that were skipped AND didn't otherwise land. */}
            {(() => {
              const skips = visibleArtifactSkips(execution.artifactSkips, artifacts.map((a) => a.type));
              if (skips.length === 0) return null;
              return (
                <Card padding="lg">
                  <div className="text-caption text-muted-foreground uppercase mb-3">
                    Captures not available
                  </div>
                  <ul className="space-y-1.5">
                    {skips.map(([kind, code]) => (
                      <li key={kind} className="text-body-sm text-muted-foreground">
                        <span className="text-foreground font-medium">{artifactKindLabel(kind)}</span>
                        {' — '}{artifactSkipReason(code)}.
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })()}
          </>
        ) : null}
      </div>
    </AppShell>
  );
}
